import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

// Target = Registration deadline (June 11, 2026 · 23:59 UTC)
const DEADLINE = new Date("2026-06-11T23:59:59Z").getTime();
// Hide ribbon entirely after this moment (June 16, 2026 · 00:00 UTC — winner announced June 15)
const HIDE_AFTER = new Date("2026-06-16T00:00:00Z").getTime();

function timeLeft() {
  const now = Date.now();
  const diff = DEADLINE - now;
  if (diff <= 0) return { diff, d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { diff, d, h, m, s };
}

export default function CompetitionRibbon() {
  const { t, lang } = useI18n();
  const [tick, setTick] = useState(timeLeft());
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("comp_ribbon_dismissed") === "1");

  useEffect(() => {
    const id = setInterval(() => setTick(timeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-hide after June 15
  if (Date.now() >= HIDE_AFTER) return null;
  if (dismissed) return null;

  const inVoting = tick.diff <= 0; // registration closed → voting / final window
  const ctaLabel = inVoting
    ? (lang === "fr" ? "Voter maintenant" : lang === "ht" ? "Vote kounye a" : "Vote now")
    : (lang === "fr" ? "S'inscrire au concours" : lang === "ht" ? "Enskri nan konkou a" : "Enter the competition");
  const headline = inVoting
    ? (lang === "fr" ? "Votes ouverts — Concours 2026" : lang === "ht" ? "Vòt ouvri — Konkou 2026" : "Voting open — Competition 2026")
    : (lang === "fr" ? "Concours 2026 · clôture le 11 juin" : lang === "ht" ? "Konkou 2026 · fèmen 11 jen" : "Competition 2026 · closes June 11");

  return (
    <div
      data-testid="competition-ribbon"
      className="sticky top-0 z-50 w-full bg-gradient-to-r from-[#0b0000] via-[#1a0000] to-[#0b0000] border-b border-[#3a0808] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center gap-3 md:gap-5">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Trophy size={16} className="text-[#d4af37]" />
          <span className="font-condensed tracking-widest uppercase text-[11px] text-[#d4af37] hidden sm:inline">
            {headline}
          </span>
          <span className="font-condensed tracking-widest uppercase text-[10px] text-[#d4af37] sm:hidden">
            2026
          </span>
        </div>

        {!inVoting && (
          <div className="flex items-center gap-1.5 md:gap-2 font-condensed tracking-wider text-xs md:text-sm" data-testid="ribbon-countdown">
            <TimeBox v={tick.d} l="D" />
            <span className="text-[#d4af37]/60">·</span>
            <TimeBox v={tick.h} l="H" />
            <span className="text-[#d4af37]/60">·</span>
            <TimeBox v={tick.m} l="M" />
            <span className="hidden sm:inline text-[#d4af37]/60">·</span>
            <TimeBox v={tick.s} l="S" className="hidden sm:flex" />
          </div>
        )}

        <Link
          to="/competition"
          data-testid="ribbon-cta"
          className="ml-auto inline-flex items-center gap-1.5 bg-[#e6192b] hover:bg-white hover:text-[#e6192b] text-white px-3 md:px-4 py-1.5 text-[11px] md:text-xs font-condensed tracking-widest uppercase transition-colors"
        >
          <span className="hidden md:inline">{ctaLabel}</span>
          <span className="md:hidden">{inVoting ? "Vote" : "Enter"}</span>
          <ArrowRight size={12} />
        </Link>

        <button
          data-testid="ribbon-dismiss"
          onClick={() => { sessionStorage.setItem("comp_ribbon_dismissed", "1"); setDismissed(true); }}
          className="text-neutral-500 hover:text-white transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function TimeBox({ v, l, className = "" }) {
  return (
    <span className={`inline-flex items-baseline gap-0.5 ${className}`}>
      <span className="text-white font-serif text-sm md:text-base leading-none">{String(v).padStart(2, "0")}</span>
      <span className="text-[9px] md:text-[10px] text-[#d4af37]/80 uppercase">{l}</span>
    </span>
  );
}
